import type { JSX } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { THEME_CSS, BRIDGE_SCRIPTS, WIDGET_CSP_META } from "./widget-theme";

/**
 * Renders an agent-generated HTML widget in a sandboxed iframe (Phase J,
 * ported from cycle-3.0). The iframe loads a fixed shell (theme + bridge); the
 * widget HTML is injected via postMessage, then inert <script> tags are
 * activated on commit. A global, once-registered bridge handles widget→host
 * messages (sendPrompt with rate-limiting, openLink, resize) and only trusts
 * iframes that mounted here.
 */

interface WidgetRendererProps {
  html: string;
  onSendPrompt: (text: string) => void;
  /** Records an unresolved-feedback signal from inside the widget (§2-4). */
  onFeedback?: ((reason: string, note?: string) => void) | undefined;
  /** Accessible name for the iframe (the widget's title). */
  title?: string;
}

const trustedFrames = new WeakSet<MessageEventSource>();
const handlerMap = new WeakMap<MessageEventSource, (text: string) => void>();
const feedbackMap = new WeakMap<MessageEventSource, (reason: string, note?: string) => void>();
const rateLimit = new WeakMap<MessageEventSource, number[]>();
const feedbackLimit = new WeakMap<MessageEventSource, number[]>();
let bridgeRegistered = false;

function registerBridge(): void {
  if (bridgeRegistered) return;
  bridgeRegistered = true;
  window.addEventListener("message", (e: MessageEvent) => {
    if (!e.source || !trustedFrames.has(e.source)) return;
    const data = e.data as { type?: string; text?: string; url?: string; reason?: string; note?: string } | null;
    if (data === null || typeof data.type !== "string") return;

    if (data.type === "feedback" && typeof data.reason === "string") {
      const reason = data.reason.trim().slice(0, 80);
      if (reason.length === 0) return;
      const now = Date.now();
      const recent = (feedbackLimit.get(e.source) ?? []).filter((t) => t > now - 10_000);
      if (recent.length >= 3) return; // max 3 / 10s per frame
      recent.push(now);
      feedbackLimit.set(e.source, recent);
      const note = typeof data.note === "string" ? data.note.slice(0, 200) : undefined;
      feedbackMap.get(e.source)?.(reason, note);
      return;
    }

    if (data.type === "sendPrompt" && typeof data.text === "string") {
      const text = data.text.trim().slice(0, 500);
      if (text.length === 0) return;
      const now = Date.now();
      const recent = (rateLimit.get(e.source) ?? []).filter((t) => t > now - 10_000);
      if (recent.length >= 3) return; // max 3 / 10s per frame
      recent.push(now);
      rateLimit.set(e.source, recent);
      handlerMap.get(e.source)?.(text);
    } else if (data.type === "openLink" && typeof data.url === "string") {
      try {
        const u = new URL(data.url);
        if (u.protocol === "http:" || u.protocol === "https:") {
          window.open(data.url, "_blank", "noopener,noreferrer");
        }
      } catch {
        /* invalid URL — ignore */
      }
    }
  });
}

// CSP meta first so it governs the document before the theme/bridge parse and
// before any commit-time script execution (§16.4).
const SHELL_SRCDOC = WIDGET_CSP_META + THEME_CSS + BRIDGE_SCRIPTS;

export function WidgetRenderer({ html, onSendPrompt, onFeedback, title }: WidgetRendererProps): JSX.Element {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const committedRef = useRef(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    registerBridge();
  }, []);

  const handleLoad = useCallback(() => {
    const cw = iframeRef.current?.contentWindow;
    if (!cw) return;
    trustedFrames.add(cw);
    handlerMap.set(cw, onSendPrompt);
    if (onFeedback) feedbackMap.set(cw, onFeedback);
    setReady(true);
  }, [onSendPrompt, onFeedback]);

  // Keep the per-frame handlers current.
  useEffect(() => {
    const cw = iframeRef.current?.contentWindow;
    if (cw) {
      handlerMap.set(cw, onSendPrompt);
      if (onFeedback) feedbackMap.set(cw, onFeedback);
    }
  }, [onSendPrompt, onFeedback]);

  // Inject the (complete) HTML then activate scripts. The host delivers the
  // full widget in one event, so we commit immediately.
  useEffect(() => {
    if (!ready || committedRef.current) return;
    const cw = iframeRef.current?.contentWindow;
    if (!cw) return;
    cw.postMessage({ type: "__wca_html", value: html }, "*");
    committedRef.current = true;
    setTimeout(() => cw.postMessage({ type: "__wca_commit" }, "*"), 30);
  }, [html, ready]);

  // Tell the iframe the host's light/dark mode so widgets match the app, and
  // keep it in sync when the user toggles the theme.
  useEffect(() => {
    if (!ready) return;
    const cw = iframeRef.current?.contentWindow;
    if (!cw) return;
    const post = (): void =>
      cw.postMessage(
        { type: "__wca_mode", mode: document.documentElement.getAttribute("data-mode") ?? "dark" },
        "*"
      );
    post();
    const obs = new MutationObserver(post);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-mode"] });
    return () => obs.disconnect();
  }, [ready]);

  // Per-instance resize.
  useEffect(() => {
    const onResize = (e: MessageEvent): void => {
      const cw = iframeRef.current?.contentWindow;
      if (!cw || e.source !== cw) return;
      const data = e.data as { type?: string; height?: number } | null;
      if (data === null || data.type !== "resize" || typeof data.height !== "number") return;
      const h = Math.max(10, Math.min(5000, data.height));
      if (iframeRef.current) iframeRef.current.style.height = `${h}px`;
    };
    window.addEventListener("message", onResize);
    return () => window.removeEventListener("message", onResize);
  }, []);

  return (
    <iframe
      ref={iframeRef}
      className="widget-frame"
      sandbox="allow-scripts allow-forms"
      srcDoc={SHELL_SRCDOC}
      onLoad={handleLoad}
      title={title !== undefined && title.length > 0 ? `交互组件：${title}` : "交互组件"}
    />
  );
}
